/**
 * Price fallback for plan products.
 *
 * The plans API returns each product's pricing in up to four sibling objects:
 *
 *   calculatedTax    calculatedTaxes     <- what the UI reads
 *   calculatedTax_   calculatedTaxes_    <- the same figures, kept as a backup copy
 *
 * They do not always agree. For EXAP Therapy Session (plan 940) the API returned
 * `calculatedTax.total = 0` while `calculatedTax_.total = 300`, and because the cart total
 * is built from `calculatedTax`, the header showed $0.00 and the Next button stayed
 * disabled - it is gated on `updatedSum > 0`.
 *
 * This fills a zero primary from whichever backup actually carries a price, so a product
 * the API priced somewhere is never treated as free.
 *
 * Applied once to the API response, so every consumer downstream - the checkbox `attr.data`
 * string, selectplan/unselectplan, the plan summary and the submitted payload - reads the
 * corrected figure without each of those call sites needing to know about the fallback.
 *
 * Only `price`, `tax` and `total` are copied. Everything else on the primary object
 * (`planProductIds`, `planProductInfos`, `taxesInfoPrime`) is left untouched, because the
 * life-insurance path reads `planProductInfos` off the primary to recompute its own
 * coverage-based pricing.
 */

/** The objects the UI reads its pricing from, in order of preference. */
const PRIMARY_KEYS = ['calculatedTax', 'calculatedTaxes'];
/** The API's backup copies, in order of preference. */
const BACKUP_KEYS = ['calculatedTax_', 'calculatedTaxes_'];

const ALL_KEYS = [...PRIMARY_KEYS, ...BACKUP_KEYS];

interface Amounts {
  price: number;
  tax: number;
  total: number;
}

/** Reads an amount that may arrive as a number or as a formatted string. */
function toAmount(value: any): number {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === 'string') {
    const n = parseFloat(value.replace(/,/g, ''));
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function readAmounts(source: any): Amounts | null {
  if (!source || typeof source !== 'object') {
    return null;
  }
  const price = toAmount(source.price);
  const tax = toAmount(source.tax);
  const total = toAmount(source.total);
  // A source only counts as usable if it actually carries money. All-zero is exactly the
  // case we are trying to repair.
  if (price <= 0 && total <= 0) {
    return null;
  }
  return { price, tax, total: total > 0 ? total : price + tax };
}

/** True when the object looks like a plan product carrying pricing objects. */
function hasPricing(node: any): boolean {
  return ALL_KEYS.some((key) => node[key] && typeof node[key] === 'object');
}

function repairProduct(product: any): void {
  // Already priced by one of the objects the UI reads - nothing to do.
  if (PRIMARY_KEYS.some((key) => readAmounts(product[key]))) {
    return;
  }

  let replacement: Amounts | null = null;
  for (const key of BACKUP_KEYS) {
    replacement = readAmounts(product[key]);
    if (replacement) {
      break;
    }
  }
  if (!replacement) {
    // Nothing anywhere carries a price. Leave the product exactly as it arrived: a
    // genuinely free product must stay free.
    return;
  }

  for (const key of PRIMARY_KEYS) {
    const primary = product[key];
    if (primary && typeof primary === 'object') {
      primary.price = replacement.price;
      primary.tax = replacement.tax;
      primary.total = replacement.total;
    }
  }
}

/**
 * Walks the plans response and repairs every product it finds, in place.
 *
 * The walk is structural rather than path-based because the same payload is read as
 * `plansData` on the gig forms, `basepackages` on the corporate forms and `rawPackages` on
 * the generic form, and the nesting differs between them. Any object carrying a
 * `calculatedTax`-style property is treated as a product; everything else is just
 * traversed.
 *
 * Returns the same reference it was given, so it can be dropped straight into an existing
 * assignment.
 */
export function applyPlanPricingFallback<T>(node: T, seen = new WeakSet<object>()): T {
  if (!node || typeof node !== 'object') {
    return node;
  }
  // Guards against a cycle in the response graph.
  if (seen.has(node as object)) {
    return node;
  }
  seen.add(node as object);

  if (Array.isArray(node)) {
    for (const item of node) {
      applyPlanPricingFallback(item, seen);
    }
    return node;
  }

  if (hasPricing(node)) {
    repairProduct(node);
  }

  for (const value of Object.values(node as Record<string, unknown>)) {
    if (value && typeof value === 'object') {
      applyPlanPricingFallback(value, seen);
    }
  }

  return node;
}
